'use client';

import { resolveLoaderStatus } from './loader-status';
import { useCycledIndex } from './use-cycled-text';
import { formatElapsed, useElapsedSeconds } from './use-elapsed-seconds';

/** The ways a caller can drive the loader's subtitle. */
export interface LoaderSubtitleOptions {
  /** The subtitle, controlled by the caller. Animates its swap whenever it changes. */
  subtitle?: string;
  /** Instead of `subtitle`, cycle through these on an interval. */
  messages?: string[];
  /** ms per message when `messages` is set. Default 3000. */
  messageIntervalMs?: number;
  /** Lines stepped by the clock: one per `stageSeconds`, holding on the last. Wins over `messages`. */
  stages?: string[];
  /** Seconds each `stages` entry holds. Default 12. */
  stageSeconds?: number;
  /** Shown once the work overruns; wins over every other line. No default, pass your own copy. */
  slowText?: string;
  /** Seconds before `slowText` takes over. Default 90. */
  slowAfterSeconds?: number;
  /** A reassurance line that appears after a beat, when there is nothing else to show. */
  hint?: string;
  /** Seconds before `hint` appears. Default 8. */
  hintAfterSeconds?: number;
  /** Epoch ms the work started; the timer and the stage/hint clock count from here. Falls back to mount. */
  startedAtMs?: number;
}

/**
 * Runs the loader's clock and resolves what the subtitle should say right now. Also collects every
 * line the caller could show, which RotatingText uses to reserve a width so the line never reflows
 * as it swaps.
 */
export const useLoaderStatus = ({
  subtitle: controlled,
  messages,
  messageIntervalMs = 3000,
  stages,
  stageSeconds = 12,
  slowText,
  slowAfterSeconds = 90,
  hint,
  hintAfterSeconds = 8,
  startedAtMs,
}: LoaderSubtitleOptions) => {
  const elapsed = useElapsedSeconds(startedAtMs);
  const cycledIndex = useCycledIndex(messages?.length ?? 0, messageIntervalMs);

  const resolved = resolveLoaderStatus(elapsed, {
    stages,
    stageSeconds,
    slowText,
    slowAfterSeconds,
    messages,
    subtitle: controlled,
    hint,
    hintAfterSeconds,
  });

  const reserved = [...(stages ?? []), ...(messages ?? []), controlled, slowText, hint].filter(
    (line): line is string => Boolean(line),
  );

  return {
    /** The subtitle to render now. Empty string when nothing drives it. */
    subtitle: resolved ?? messages?.[cycledIndex] ?? '',
    time: formatElapsed(elapsed),
    reserveFor: reserved.length ? reserved : undefined,
  };
};
